import { useEffect, useState } from 'react';
import { Input } from '@adop/ui';
import { Check, Loader2, X } from 'lucide-react';
import { RESERVED_SLUGS } from '@adop/shared';
import { apiFetch, errorMessage } from '../lib/api';
import type { SlugCheckResponse } from '@adop/shared';

export interface SlugInputProps {
  value: string;
  onChange: (value: string) => void;
  locationId?: string;
  error?: string;
  disabled?: boolean;
}

type Status = { kind: 'idle' } | { kind: 'checking' } | { kind: 'available' } | { kind: 'taken'; reason?: string };

/**
 * Live web-address picker. Uses the platform API so the reserved list and
 * "used recently" redirect window are enforced consistently in one place.
 */
export function SlugInput({ value, onChange, locationId, error, disabled }: SlugInputProps) {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  useEffect(() => {
    const slug = value.trim().toLowerCase();
    if (slug.length === 0) {
      setStatus({ kind: 'idle' });
      return;
    }
    let active = true;
    setStatus({ kind: 'checking' });
    const timer = window.setTimeout(() => {
      apiFetch<SlugCheckResponse>(
        `/api/platform/slugs/check?slug=${encodeURIComponent(slug)}${
          locationId ? `&location_id=${encodeURIComponent(locationId)}` : ''
        }`,
      )
        .then((response) => {
          if (!active) return;
          setStatus(
            response.available
              ? { kind: 'available' }
              : { kind: 'taken', ...(response.reason ? { reason: response.reason } : {}) },
          );
        })
        .catch((cause: unknown) => {
          if (!active) return;
          setStatus({ kind: 'taken', reason: errorMessage(cause) });
        });
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [value, locationId]);

  const hint =
    status.kind === 'checking' ? (
      <span className="inline-flex items-center gap-1 text-zinc-500">
        <Loader2 className="h-3 w-3 animate-spin" /> Checking…
      </span>
    ) : status.kind === 'available' ? (
      <span className="inline-flex items-center gap-1 text-emerald-600">
        <Check className="h-3 w-3" /> Available
      </span>
    ) : status.kind === 'taken' ? (
      <span className="inline-flex items-center gap-1 text-red-600">
        <X className="h-3 w-3" /> {status.reason ?? 'That web address is taken.'}
      </span>
    ) : (
      `Reserved: ${RESERVED_SLUGS.slice(0, 5).join(', ')}…`
    );

  return (
    <div className="space-y-2">
      <div className="flex items-stretch gap-2">
        <Input
          value={value}
          disabled={disabled}
          error={error}
          placeholder="your-restaurant"
          onChange={(event) => onChange(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
          className="flex-1"
          aria-label="Web address"
        />
        <span className="inline-flex items-center whitespace-nowrap text-sm text-zinc-500">
          .adop.app
        </span>
      </div>
      <p className="text-xs">{hint}</p>
    </div>
  );
}
